import { Injectable } from '@angular/core';
import { HttpService } from 'src/app/shared/service/http.service';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { DepositCrypto } from 'src/app/shared/models/deposit-crypto.model';
import { Pagination } from 'src/app/shared/models/pagination.model';
import { catchError, map } from 'rxjs/operators';
import { DepositRial } from 'src/app/shared/models/deposit-rial.model';
import { WithdrawModel } from 'src/app/shared/models/withdraw.model';
import { WithdrawRial } from 'src/app/shared/models/withdraw-rial.model';
import {
  GiftCardContainer,
  GiftCardCurrency,
  GiftCardStatus,
  GiftCardUser
} from '../models/gift-card.model';
import { Utility } from '../../core/interceptor/utility';
import { DipositTreasury } from '../models/deposit-trasury.model';
import { WithdrawTreasury } from '../models/withdraw-treasury.model';
import { DepositCryptoDto } from '../models/deposit-crypto.dto.model';
import {
  DateFilterWithdrawCrypto,
  DateFilterWithdrawCryptoDto,
  toDomainDateFilterDto
} from '../../core/models/date-filter-withdraw-crypto.entities';
import { HttpError } from '../models/http-error.model';
import Swal from 'sweetalert2';
import {
  ReportAccessList,
  ReportInputModel,
  ReportListModel,
  ReportOutputModel,
  ReportQueueList,
  ReportResult
} from '../models/report.model';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  private _validationErrorsWithdrawCrypto$ = new BehaviorSubject<{}>({});
  validationErrorsWithdrawCrypto$ = this._validationErrorsWithdrawCrypto$.asObservable();

  private Toast = Swal.mixin({
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });

  constructor(private http: HttpService) {
  }

  getGiftCards(
    createdGiftCardUserIds: number[],
    redeemedGiftCardUserIds: number[],
    status: GiftCardStatus | null,
    currencyId: string[],
    page: number,
    limit = 15
  ): Observable<Pagination<GiftCardContainer>> {
    return this.http
      .request('post', '/v1/admin/gift-card', {
        usedByUserId: redeemedGiftCardUserIds,
        userId: createdGiftCardUserIds,
        status,
        currencyId,
        page,
        limit
      })
      .pipe(
        map((response: any) => {
          return new Pagination<any>(response);
        }),
        map((pagination: Pagination<any>) => {
          pagination.data = pagination.data.map((each) => {
            return {
              createdByUser: Utility.camelize(each.created_by_user) as GiftCardUser,
              redeemedByUser: Utility.camelize(each.redeemed_by_user) as GiftCardUser,
              currency: Utility.camelize(each.currency) as GiftCardCurrency,
              giftcard: {
                amount: each.giftcard.amount,
                status: each.giftcard.status as GiftCardStatus,
                amountIRT: Math.floor((each.giftcard.amount_irr ?? 0) / 10),
                code: each.giftcard.code,
                createdAt: each.giftcard.created_at,
                description: each.giftcard.description
              }
            } as GiftCardContainer;
          });
          return pagination;
        })
      );
  }

  getCryptoDepositList(
    userId: number[],
    currencyId: string[],
    showManualDeposits: boolean,
    page: number,
    limit = 15
  ): Observable<Pagination<DepositCrypto>> {
    return this.http
      .request('post', '/v1/admin/deposit/crypto', {
        userId,
        currencyId,
        manual_transaction: showManualDeposits,
        page,
        limit
      })
      .pipe(
        map((response: Pagination<DepositCryptoDto>) => {
          response.data = response.data?.map(
            (order: DepositCryptoDto) => new DepositCrypto(order)
          ) as unknown as DepositCryptoDto[];
          return response;
        }),
        map((response) => new Pagination<DepositCrypto>(response))
      );
  }

  getRialDepositList(
    userId: number[],
    page: number,
    limit = 15
  ): Observable<Pagination<DepositRial>> {
    return this.http
      .request('post', '/v1/admin/deposit/rial', {
        userId,
        page,
        limit
      })
      .pipe(
        map((response: any) => {
          response.data = response.data?.map((order: any) => new DepositRial(order));
          return response;
        }),
        map((response) => new Pagination<DepositRial>(response))
      );
  }

  getCryptoWithdrawList(
    userId: number[],
    currencyId: string[],
    dateFilter: DateFilterWithdrawCrypto,
    page: number,
    limit = 15
  ): Observable<Pagination<WithdrawModel>> {
    const timezoneOffset = Math.abs(new Date().getTimezoneOffset());
    const toDateFilterDto = toDomainDateFilterDto(dateFilter);
    this._validationErrorsWithdrawCrypto$.next({});
    return this.http
      .request('post', '/v2/admin/withdraw/crypto', {
        userId,
        timezone_offset: timezoneOffset,
        currencyId,
        ...toDateFilterDto,
        page,
        limit
      })
      .pipe(
        catchError((err: HttpError) => {
          if (err.status === 422) {
            this._validationErrorsWithdrawCrypto$.next(err.validationError);
          } else {
            this.Toast.fire({
              text: err.error,
              icon: 'error'
            });
          }

          throw undefined;
        }),
        map((response: any) => {
          response.data = response.data?.map((order: any) => new WithdrawModel(order));
          return response;
        }),
        map((response) => new Pagination<WithdrawModel>(response))
      );
  }

  getRialWithdrawList(
    userId: number[],
    page: number,
    limit = 15,
    status: string | null = null
  ): Observable<Pagination<WithdrawRial>> {
    return this.http
      .request('post', '/v1/admin/withdraw/rial', {
        userId,
        page,
        limit,
        status
      })
      .pipe(
        map((response: any) => {
          response.data = response.data?.map((order: any) => new WithdrawRial(order));
          return response;
        }),
        map((response) => new Pagination<WithdrawRial>(response))
      );
  }

  getTreasuryRialWithdrawList(
    user_id: number[],
    limit = 15,
    status: string | null = null,
    start_time: string,
    end_time: string,
    succeed: boolean,
    timezone_offset: number
  ): Observable<Pagination<WithdrawTreasury>> {
    return this.http
      .request('post', '/v1/admin/withdraw/rial/treasury', {
        user_id,
        limit,
        status,
        start_time,
        end_time,
        succeed,
        timezone_offset
      })
      .pipe(
        map((response: any) => {
          response.data = response.data?.map((order: any) => new WithdrawTreasury(order));
          return response;
        }),
        map((response) => new Pagination<WithdrawTreasury>(response))
      );
  }

  getTreasuryRialDepositList(
    user_id: number[],
    limit = 15,
    status: string | null = null,
    start_time: string,
    end_time: string,
    succeed: boolean,
    timezone_offset: number,
    type: string
  ): Observable<Pagination<DipositTreasury>> {
    return this.http
      .request('post', '/v1/admin/deposit/rial/treasury', {
        user_id,
        limit,
        status,
        start_time,
        end_time,
        succeed,
        timezone_offset,
        type
      })
      .pipe(
        map((response: any) => {
          response.data = response.data?.map((order: any) => new DipositTreasury(order));
          return response;
        }),
        map((response) => new Pagination<DipositTreasury>(response))
      );
  }

  getGeneralReportList(): Observable<ReportListModel[]> {
    return this.http.request('get', '/v1/admin/reports').pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map(each => new ReportListModel(each)))
    );
  }

  getSingleReport(id: string): Observable<ReportListModel> {
    return this.http.request('get', '/v1/admin/reports/' + id).pipe(
      map((response: any) => new ReportListModel(response.data))
    );
  }

  getReportFilter(id: string): Observable<ReportInputModel[]> {
    return this.http.request('get', `/v1/admin/reports/${id}/inputs`).pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map(each => new ReportInputModel(each)))
    );
  }

  getReportOutput(id: string): Observable<ReportOutputModel[]> {
    return this.http.request('get', `/v1/admin/reports/${id}/outputs`).pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map(each => new ReportInputModel(each)))
    );
  }

  getReportResult(id: string, payload: unknown): Observable<ReportResult[]> {
    return this.http.request('post', `/v1/admin/reports/${id}/results`, payload).pipe(
      map((response: any) => response.data as ReportResult[])
    );
  }

  sendUserApiResult(url: string, method: string, payload: any): Observable<void> {
    return this.http.request(method.toLowerCase(), url, payload);
  }

  getReportAccessList(): Observable<ReportAccessList[]> {
    return this.http.request('get', `/v1/admin/reports-permissions`).pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map(each => new ReportAccessList(each)))
    );
  }

  grantUserToReport(reportId: string, userId: number) {
    return this.http.request('post', '/v1/admin/reports-permissions', {
      id: reportId,
      user_id: userId
    });
  }

  getReportQueueList(): Observable<ReportQueueList[]> {
    return this.http.request('get', '/v1/admin/reports-queue').pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map(each => new ReportQueueList(each)))
    );
  }

  grantScopeToReport(reportId: string, scopeId: string) {
    return this.http.request('post', '/v1/admin/reports-permissions', {
      id: reportId,
      scope_id: scopeId,
      user_id: null
    });
  }

  removeAccess(reportId: string, id: string | number, type: 'scope' | 'user'): Observable<void> {
    return this.http.request('delete', `/v1/admin/reports-permissions?id=${reportId}&user_id=${type === 'user' ? id : ''}&scope_id=${type === 'scope' ? id : ''}`);
  }
}
